import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Autocomplete
} from '@mui/material';
import * as XLSX from 'xlsx';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { API_BASE_URL, getAuthHeaders } from '../../services/api';
import { format } from 'date-fns';

// Use a default color scheme if styleGuide is not available
const colors = {
  tableHeaderBackground: '#f5f5f5',
  lightBackground: '#f9f9f9',
  success: '#4caf50',
  error: '#f44336'
};

const API_URL = API_BASE_URL;

const GeneralLedger = () => {
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [accounts, setAccounts] = useState([]);
  
  // Filter states
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [dealNumber, setDealNumber] = useState('');
  const [dealOptions, setDealOptions] = useState([]);
  const [dealOptionsLoading, setDealOptionsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Deal number suggestions while typing (debounced)
  useEffect(() => {
    const q = dealNumber.trim();
    if (q.length < 2) {
      setDealOptions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setDealOptionsLoading(true);
      try {
        const response = await axios.get(`${API_URL}/accounting/general-ledger/deal-numbers`, {
          params: { q },
          headers: getAuthHeaders()
        });
        if (!cancelled) setDealOptions(response.data || []);
      } catch (err) {
        console.error('Error fetching deal numbers:', err);
      } finally {
        if (!cancelled) setDealOptionsLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [dealNumber]);

  // Fetch chart of accounts for filter dropdown
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await axios.get(`${API_URL}/accounting/chart-of-accounts`, {
          headers: getAuthHeaders()
        });
        setAccounts(response.data);
      } catch (err) {
        console.error('Error fetching accounts:', err);
      }
    };
    
    fetchAccounts();
  }, []);
  
  // Current filter values as API params; `cleared` ignores them (used by Clear)
  const buildFilterParams = (cleared = false) => {
    const params = {};
    if (cleared) return params;

    if (startDate) {
      params.startDate = format(startDate, 'yyyy-MM-dd');
    }

    if (endDate) {
      params.endDate = format(endDate, 'yyyy-MM-dd');
    }

    if (selectedAccount) {
      params.accountId = selectedAccount;
    }

    if (dealNumber.trim()) {
      params.dealNumber = dealNumber.trim();
    }
    return params;
  };

  // Fetch ledger entries with filters
  const fetchLedgerEntries = async ({ cleared = false, pageOverride, dealOverride } = {}) => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        ...buildFilterParams(cleared),
        ...(dealOverride ? { dealNumber: dealOverride } : {}),
        limit: rowsPerPage,
        offset: (pageOverride !== undefined ? pageOverride : page) * rowsPerPage
      };

      const response = await axios.get(`${API_URL}/accounting/general-ledger`, {
        params,
        headers: getAuthHeaders()
      });
      setLedgerEntries(response.data.entries);
      setTotal(response.data.total);
    } catch (err) {
      console.error('Error fetching ledger entries:', err);
      setError('Failed to load general ledger. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Initial fetch
  useEffect(() => {
    fetchLedgerEntries();
  }, [page, rowsPerPage]);
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleFilter = () => {
    setPage(0);
    fetchLedgerEntries({ pageOverride: 0 });
  };

  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedAccount('');
    setDealNumber('');
    setPage(0);
    fetchLedgerEntries({ cleared: true, pageOverride: 0 });
  };

  // Export every entry matching the current filters (not just this page) to Excel
  const handleExport = async () => {
    setExporting(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/accounting/general-ledger`, {
        params: { ...buildFilterParams(), exportAll: true },
        headers: getAuthHeaders()
      });
      const entries = response.data.entries || [];
      if (entries.length === 0) {
        setError('No ledger entries to export for these filters.');
        return;
      }
      const rows = entries.map((entry) => ({
        Date: format(new Date(entry.entry_date), 'yyyy-MM-dd'),
        'Deal Number': entry.deal_number || '',
        'Account Code': entry.account_code || '',
        'Account Name': entry.account_name || '',
        Description: entry.description || '',
        Debit: Number(entry.debit_amount) || 0,
        Credit: Number(entry.credit_amount) || 0,
        Currency: entry.currency || 'LKR',
        Transaction: entry.transaction_code || (entry.transaction_id ? `#${entry.transaction_id}` : '')
      }));
      const totalDebit = rows.reduce((sum, r) => sum + r.Debit, 0);
      const totalCredit = rows.reduce((sum, r) => sum + r.Credit, 0);
      rows.push({ Description: 'Total', Debit: totalDebit, Credit: totalCredit });

      const sheet = XLSX.utils.json_to_sheet(rows);
      sheet['!cols'] = [
        { wch: 12 }, { wch: 28 }, { wch: 20 }, { wch: 40 }, { wch: 60 },
        { wch: 16 }, { wch: 16 }, { wch: 8 }, { wch: 16 }
      ];
      const range = XLSX.utils.decode_range(sheet['!ref']);
      for (let r = 1; r <= range.e.r; r += 1) {
        ['F', 'G'].forEach((col) => {
          const cell = sheet[`${col}${r + 1}`];
          if (cell) cell.z = '#,##0.00';
        });
      }
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, 'General Ledger');

      const { startDate: from, endDate: to } = response.data;
      const dealPart = dealNumber.trim() ? `_${dealNumber.trim().replace(/[\\/:*?"<>|]/g, '-')}` : '';
      const datePart = from || to ? `_${from || 'start'}_to_${to || 'end'}` : '';
      XLSX.writeFile(workbook, `General_Ledger${dealPart}${datePart}.xlsx`);
    } catch (err) {
      console.error('Error exporting ledger entries:', err);
      setError('Failed to export the general ledger. Please try again.');
    } finally {
      setExporting(false);
    }
  };
  
  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        General Ledger
      </Typography>
      
      <Paper sx={{ p: 2, mb: 3 }} elevation={2}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                renderInput={(params) => <TextField {...params} fullWidth />}
                slotProps={{
                  textField: { fullWidth: true }
                }}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                renderInput={(params) => <TextField {...params} fullWidth />}
                slotProps={{
                  textField: { fullWidth: true }
                }}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel id="account-select-label">Account</InputLabel>
              <Select
                labelId="account-select-label"
                id="account-select"
                value={selectedAccount}
                label="Account"
                onChange={(e) => setSelectedAccount(e.target.value)}
              >
                <MenuItem value="">
                  <em>All Accounts</em>
                </MenuItem>
                {accounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.account_code} - {account.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              freeSolo
              sx={{ width: 300 }}
              slotProps={{ popper: { sx: { minWidth: 340 } } }}
              options={dealOptions}
              loading={dealOptionsLoading}
              filterOptions={(options) => options}
              inputValue={dealNumber}
              onInputChange={(event, value) => setDealNumber(value || '')}
              onChange={(event, value) => {
                // Picking a suggestion (or Enter on typed text) searches straight away
                if (typeof value === 'string' && value.trim()) {
                  setDealNumber(value);
                  setPage(0);
                  fetchLedgerEntries({ pageOverride: 0, dealOverride: value.trim() });
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Deal Number"
                  placeholder="Type to search…"
                  fullWidth
                />
              )}
              noOptionsText={dealNumber.trim().length < 2 ? 'Type at least 2 characters' : 'No matching deals'}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleFilter}
                disabled={loading}
              >
                Apply Filters
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                disabled={loading}
              >
                Clear
              </Button>
              <Button
                variant="outlined"
                color="success"
                onClick={handleExport}
                disabled={loading || exporting}
              >
                {exporting ? 'Exporting…' : 'Export to Excel'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="general ledger table">
              <TableHead>
                <TableRow sx={{ backgroundColor: colors.tableHeaderBackground }}>
                  <TableCell>Date</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Debit</TableCell>
                  <TableCell align="right">Credit</TableCell>
                  <TableCell>Transaction</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ledgerEntries.length > 0 ? (
                  ledgerEntries.map((entry) => (
                    <TableRow key={entry.id} hover>
                      <TableCell>{format(new Date(entry.entry_date), 'yyyy-MM-dd')}</TableCell>
                      <TableCell>
                        {entry.account_code ? `${entry.account_code} - ${entry.account_name || 'Unknown'}` : `Account ID: ${entry.account_id} (Account not found)`}
                      </TableCell>
                      <TableCell>{entry.description}</TableCell>
                      <TableCell align="right">
                        {entry.debit_amount > 0 ? 
                          new Intl.NumberFormat('en-US', { 
                            style: 'currency', 
                            currency: entry.currency || 'LKR' 
                          }).format(entry.debit_amount) : ''}
                      </TableCell>
                      <TableCell align="right">
                        {entry.credit_amount > 0 ? 
                          new Intl.NumberFormat('en-US', { 
                            style: 'currency', 
                            currency: entry.currency || 'LKR' 
                          }).format(entry.credit_amount) : ''}
                      </TableCell>
                      <TableCell>
                        {entry.transaction_code || 
                         (entry.deal_number ? `Deal: ${entry.deal_number}` : 
                          (entry.transaction_id ? `Transaction #${entry.transaction_id}` : '-'))}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No ledger entries found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}
    </Box>
  );
};

export default GeneralLedger;
